import React from 'react'
import moment from 'moment'
import Highcharts from 'highcharts'
import HighchartsExporting from 'highcharts/modules/exporting'
import { Grid, Segment } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getGMSStatsCsv, getWholesalerOrderInfoCsv, listAppointments, listDashboard } from 'app-support/stats/state/requests'
import { apiFormat } from '@cbgms/base/utils/datetime'
import '../../../../assets/styles/dashboard.less'
import { ChartHeader } from '../ChartHeader'
import MonitorAccountsChart from '../Charts/Monitor/MonitorAccountsChart'
import MonitorAppointmentsChart from '../Charts/Monitor/MonitorAppointmentsChart'
import MonitorInvoicesChart from '../Charts/Monitor/MonitorInvoicesChart'
import MonitorOrdersChart from '../Charts/Monitor/MonitorOrdersChart'
import MonitorOrderslinesChart from '../Charts/Monitor/MonitorOrderlinesChart'
import MonitorVehiclesChart from '../Charts/Monitor/MonitorVehiclesChart'
import MonitorShopsChart from '../Charts/Monitor/MonitorShopsChart'
import { DashboardDownloadSegment } from '../DashboardDownloadSegment'
import { DashboardNumberSegment } from '../DashboardNumberSegment'
import MonitorVehicleInvoicesChart from '../Charts/Monitor/MonitorVehicleInvoicesChart'
import { useNotifier } from '@cbgms/base/notifier'
import { Redirect } from 'react-router-dom'
import { IRootReducerState } from 'state/reducers'
import { useQuery } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import StatsLayout from '../StatsLayout'

// Export of Highcharts
if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts)
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { startDate, endDate, country, shopModules, companyUUID, shopID } = useSelector((state: IRootReducerState) => state.stats)
  const formattedStartDate = moment(startDate, 'DD-MM-YYYY').format(apiFormat.yearMonthDay)
  const formattedEndDate = moment(endDate, 'DD-MM-YYYY').format(apiFormat.yearMonthDay)

  const dashboardResult = useQuery(
    ['Statistics', 'Dashboard', { startDate, endDate, country, shopModules, companyUUID, shopID }],
    async () => {
      const [dashboardResult, appointmentsResult] = await Promise.all([
        dispatch(
          listDashboard({
            CompanyUUID: companyUUID,
            EndDate: formattedEndDate,
            ShopID: shopID,
            StartDate: formattedStartDate,
            TimeFrame: 'month',
            Country: country,
            ShopModules: shopModules
          })
        ),
        dispatch(
          listAppointments({
            CompanyUUID: companyUUID,
            EndDate: formattedEndDate,
            ShopID: shopID,
            StartDate: formattedStartDate,
            TimeFrame: 'month',
            Country: country,
            ShopModules: shopModules
          })
        )
      ])

      return {
        search: dashboardResult.Data,
        monitor: dashboardResult.Data,
        appointments: appointmentsResult.Data
      }
    },
    { refetchOnWindowFocus: false, refetchOnMount: false }
  )

  if (dashboardResult.isFetched && isEmpty(dashboardResult.data)) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/stats' />
  }

  const defaultValue = {
    totalShops: 0,
    totalVehicles: 0,
    totalOrders: 0,
    totalOrderlines: 0,
    totalAccounts: 0,
    totalAppointments: 0,
    totalInvoices: 0,
    totalVehicleInvoices: 0
  }

  const { totalShops, totalVehicles, totalOrders, totalOrderlines, totalAccounts, totalAppointments, totalInvoices, totalVehicleInvoices } =
    dashboardResult.data?.search.reduce((previousValue, currentValue) => {
      return {
        totalShops: previousValue.totalShops + currentValue.TotalShops,
        totalVehicles: previousValue.totalVehicles + currentValue.TotalVehicles,
        totalOrders: previousValue.totalOrders + currentValue.TotalOrders,
        totalOrderlines: previousValue.totalOrderlines + currentValue.TotalOrderLines,
        totalAccounts: previousValue.totalAccounts + currentValue.TotalAccounts,
        totalAppointments: previousValue.totalAppointments + currentValue.TotalAppointments,
        totalInvoices: previousValue.totalInvoices + currentValue.TotalInvoices,
        totalVehicleInvoices: previousValue.totalVehicleInvoices + currentValue.TotalVehicleInvoices
      }
    }, defaultValue) || defaultValue

  const handleDownloadWholesalerOrderInfo = async () => {
    const response = await dispatch(
      getWholesalerOrderInfoCsv({
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        ShopModules: shopModules,
        Country: country
      })
    )
    return new Blob([response], { type: 'text/csv' })
  }

  const handleDownloadGMSStats = async () => {
    const response = await dispatch(
      getGMSStatsCsv({
        StartDate: formattedStartDate,
        EndDate: formattedEndDate,
        ShopModules: shopModules,
        Country: country
      })
    )
    return new Blob([response], { type: 'text/csv' })
  }

  return (
    <StatsLayout testId='segment-dashboard-stats' title={t('global.stats.dashboard')} backButton={false}>
      <Grid.Row columns='5'>
        <Grid.Column>
          <DashboardNumberSegment link='/stats/vehicles' title={t('global.stats.vehicles')} icon='car' value={totalVehicles} />
        </Grid.Column>
        <Grid.Column>
          <DashboardNumberSegment link='/stats/orders' title={t('global.stats.orders')} icon='dolly' value={totalOrders} />
        </Grid.Column>
        <Grid.Column>
          <DashboardNumberSegment
            link='/stats/orderlines'
            title={t('global.stats.orderlines')}
            icon='dolly flatbed'
            value={totalOrderlines}
          />
        </Grid.Column>
        {!shopID && (
          <Grid.Column>
            <DashboardNumberSegment link='/stats/shops' title={t('global.stats.shops')} icon='warehouse' value={totalShops} />
          </Grid.Column>
        )}
        <Grid.Column>
          <DashboardNumberSegment link='/stats/accounts' title={t('global.stats.accounts')} icon='users' value={totalAccounts} />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns='5'>
        <Grid.Column>
          <DashboardNumberSegment
            link='/stats/appointments'
            title={t('global.stats.appointments')}
            icon='calendar alternate outline'
            value={totalAppointments}
          />
        </Grid.Column>
        <Grid.Column>
          <DashboardNumberSegment link='/stats/invoices' title={t('global.stats.invoices')} icon='file alternate' value={totalInvoices} />
        </Grid.Column>
        <Grid.Column>
          <DashboardNumberSegment
            link='/stats/vehicle-invoices'
            title={t('global.stats.vehicle_invoices')}
            icon='file alternate'
            value={totalVehicleInvoices}
          />
        </Grid.Column>
        <Grid.Column>
          <DashboardDownloadSegment
            title={t('global.stats.wholesaler')}
            subTitle={t('global.stats.order_info')}
            icon='file excel'
            downloadAction={handleDownloadWholesalerOrderInfo}
            fileName='wholesaler-order-info.csv'
          />
        </Grid.Column>
        <Grid.Column>
          <DashboardDownloadSegment
            title={t('global.stats.gms_statistics')}
            subTitle={t('global.stats.gms_statistics_subtitle')}
            icon='file excel'
            downloadAction={handleDownloadGMSStats}
            fileName='gms-info.csv'
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <hr color='#cc0000' />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.vehicles')} loading={dashboardResult.isLoading} />
            <MonitorVehiclesChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.orders')} loading={dashboardResult.isLoading} />
            <MonitorOrdersChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.orderlines')} loading={dashboardResult.isLoading} />
            <MonitorOrderslinesChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
        {!shopID && (
          <Grid.Column>
            <Segment>
              <ChartHeader title={t('global.stats.shops')} loading={dashboardResult.isLoading} />
              <MonitorShopsChart data={dashboardResult.data?.monitor || []} />
            </Segment>
          </Grid.Column>
        )}
      </Grid.Row>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.accounts')} loading={dashboardResult.isLoading} />
            <MonitorAccountsChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.appointments')} loading={dashboardResult.isLoading} />
            <MonitorAppointmentsChart data={dashboardResult.data?.appointments || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.invoices')} loading={dashboardResult.isLoading} />
            <MonitorInvoicesChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment>
            <ChartHeader title={t('global.stats.vehicle_invoices')} loading={dashboardResult.isLoading} />
            <MonitorVehicleInvoicesChart data={dashboardResult.data?.monitor || []} />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </StatsLayout>
  )
}

export default Dashboard
